"use client";

import useResize from "@/app/hooks/useResize";
import { Box, Button, colors, useTheme } from "@mui/material";
import RenderQuillContent from "@v2/components/RenderQuillContent";
import CenterBox from "@v2/components/styled/CenterBox";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

interface QuillLCSettingsType {
    label: string
    full_width: boolean,
    max_height: string
}
interface QuillLCDataType {
    content: string | null
}

export default function QuillLC({ content, embeddedDepth = 0 }: {
    content: {
        data: QuillLCDataType,
        settings: QuillLCSettingsType
    };
    embeddedDepth?: number;
}) {

    const elementRef = useRef<HTMLDivElement>(null)
    const sectionRef = useRef<HTMLDivElement>(null)
    // Only a close the reader asked for is worth anchoring. Collapsing on a route
    // change must leave the scroll to the router.
    const anchorAfterCollapse = useRef(false)
    const pathname = usePathname()
    const [open, setOpen] = useState(false)
    const [height, setHeight] = useState<number>(0)
    const hasMoreButton = height > parseInt(content.settings.max_height);

    useEffect(() => {
        setHeight(elementRef.current?.clientHeight ?? 0)
    }, [elementRef.current])

    const toggleOpen = useCallback(() => {
        setOpen(o => {
            anchorAfterCollapse.current = o
            return !o
        })
    }, [])

    // Closing takes the expanded height out of the page above the reader, so the
    // scroll position they kept now points at whatever moved up into it, which
    // reads as the page jumping to its end. Putting the block back under the top
    // of the viewport lands them where they were reading.
    useLayoutEffect(() => {
        if (open || !anchorAfterCollapse.current) return;
        anchorAfterCollapse.current = false;

        const top = sectionRef.current?.getBoundingClientRect().top;
        // A block still on screen has not lost anything above the reader.
        if (top === undefined || top >= 0) return;

        window.scrollTo(0, Math.max(0, top + window.scrollY));
    }, [open])

    const windowSize = useResize()
    useEffect(() => {
        setHeight(elementRef.current?.clientHeight ?? 0)

    }, [windowSize])
    useEffect(() => {
        setOpen(false)
        setHeight(elementRef.current?.clientHeight)
    }, [pathname])
    if (!content.data.content) return null;
    return (
        <Box ref={sectionRef} sx={{
            maxWidth: content.settings.full_width ? 'none' : 1400,
            margin: 'auto',
            px: content.settings.full_width ? 0 : { xs: 2, lg2: 0 },
        }}>
            <Box className={open ? 'show_full' : undefined} sx={{
                maxHeight: `${content.settings.max_height}px`,
                overflow: 'hidden',
                // transition: `${height / 4}ms`,
                position: 'relative',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    right: 0, left: 0, bottom: 0,
                    height: 100,
                    background: hasMoreButton ? 'linear-gradient(0deg, #fff, transparent)' : undefined,
                },
                '&.show_full': {
                    maxHeight: 'unset',
                    '&::after': {
                        background: 'unset',
                    }
                },

                }}>
                <div ref={elementRef}>
                    <RenderQuillContent html={content.data.content} embeddedDepth={embeddedDepth} />
                </div>
                {hasMoreButton && (
                    <CenterBox sx={{
                        py: 2,
                        width: 1,
                        position: !open ? 'absolute' : 'sticky',
                        bottom: 0,
                        zIndex: 2,
                    }}>
                        <Button variant="outlined" color="inherit" onClick={toggleOpen} sx={{
                            height: 48,
                            width: 190,
                            bgcolor: '#fff',
                            color: colors.grey[900],
                            fontWeight: 400

                        }}>
                            {open ? 'بستن' : 'مشاهده بیشتر'}
                        </Button>
                    </CenterBox>
                )}
            </Box>

        </Box>
    )
}
